import React, { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform, useSpring, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import InteractiveLinesSif from "./InteractiveLines-sif";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

const SectionLabel: React.FC<{ title: string; dark?: boolean }> = ({ title, dark }) => (
  <div className="mb-6 md:mb-8">
    <h2 className={`text-2xl sm:text-3xl md:text-4xl font-serif font-bold ${dark ? "text-white" : "text-black"}`}>{title}</h2>
  </div>
);

const Divider: React.FC<{ dark?: boolean }> = ({ dark }) => null;


const VideoPlayer: React.FC<{ videoId: string; title: string }> = ({ videoId, title }) => {
  const [playing, setPlaying] = useState(false);
  return (
    <div className="w-full mt-4">
      <div className="aspect-video w-full rounded-2xl overflow-hidden relative cursor-pointer group">
        {playing ? (
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        ) : (
          <div onClick={() => setPlaying(true)} className="w-full h-full relative">
            <img
              src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
              alt={title}
              className="w-full h-full object-cover"
            />
            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/90 rounded-full flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-black ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const CarPlaySlider: React.FC = () => {
  const [current, setCurrent] = useState(0);
  const images = ["/carplay1.png", "/carplay2.png", "/carplay3.png"];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % images.length);
    }, 2500);
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="px-8 sm:px-16 md:px-32 mb-6">
      <div className="relative w-full overflow-hidden rounded-2xl">
        <div
          className="flex transition-transform duration-700 ease-in-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {images.map((src, i) => (
            <img key={i} src={src} alt={`CarPlay screen ${i + 1}`} className="w-full flex-shrink-0 h-auto object-cover" />
          ))}
        </div>
        {/* Dots */}
        <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-2">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${i === current ? "bg-white scale-125" : "bg-white/50"}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const SlowisFast: React.FC = () => {
  const { scrollY } = useScroll();
  const scrollX = useTransform(scrollY, [0, 400], [0, -200]);
  const xSpring = useSpring(scrollX, { stiffness: 100, damping: 30 });

  const heroRef = useRef(null);
  const gradientSectionRef = useRef(null);
  const aboutSectionRef = useRef(null);

  const heroInView = useInView(heroRef, { once: true, amount: 0.3 });
  const gradientSectionInView = useInView(gradientSectionRef, { once: true, amount: 0.3 });
  const aboutSectionInView = useInView(aboutSectionRef, { once: true, amount: 0.3 });

  return (
    <div className="relative min-h-screen bg-[#F5F5F0]">
      <Header />

      {/* ── Hero ── */}
      <motion.section
        ref={heroRef}
        className="relative w-full min-h-[100vh] h-screen overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: heroInView ? 1 : 0 }}
        transition={{ duration: 1 }}
      >
        <InteractiveLinesSif />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            className="relative z-20 translate-y-50"
            animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
          >
            <motion.img
              src="/sif-card.png"
              alt="Slow is Fast"
              className="w-72 h-auto"
              whileHover={{ scale: 1.1, transition: { duration: 0.3, ease: "easeOut" } }}
            />
          </motion.div>
        </div>
        <motion.h1
          className="relative z-10 text-[28vw] sm:text-[25vw] md:text-[20vw] font-serif font-bold text-right mt-[60vh] md:mt-[45vh] pr-[5vw] whitespace-nowrap"
          style={{ x: xSpring }}
        >
          SLOW IS FAST
        </motion.h1>
        <div className="absolute bottom-0 left-0 right-0 h-[30vh] bg-gradient-to-t from-[#F5F5F0] to-transparent z-40" />
      </motion.section>

      {/* ── Gradient / Marquee ── */}
      <motion.section
        ref={gradientSectionRef}
        className="relative w-full bg-[#E2E5F8] py-10 flex flex-col items-center justify-start overflow-hidden"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: gradientSectionInView ? 1 : 0, y: gradientSectionInView ? 0 : 50 }}
        transition={{ duration: 1 }}
      >
        <div className="w-full overflow-hidden mb-12">
          <motion.div
            className="whitespace-nowrap text-xl sm:text-2xl md:text-4xl font-light italic font-inter text-black"
            initial={{ x: "100%" }}
            animate={{ x: "-100%" }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
          >
            UX Research · UI/UX Design · Prototyping
          </motion.div>
        </div>
        <motion.img
          src="/sif-1.png"
          alt="Slow is Fast preview"
          className="w-full rounded-none"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: gradientSectionInView ? 1 : 0, y: gradientSectionInView ? 0 : 30 }}
          transition={{ duration: 1, delay: 0.3 }}
        />
      </motion.section>

      {/* ── 1. Project Overview ── */}
      <motion.section
        ref={aboutSectionRef}
        className="relative w-full bg-[#F5F5F0] pt-14 pb-8 sm:pt-16 sm:pb-10 md:pt-20 md:pb-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden"
        animate={aboutSectionInView ? "visible" : "hidden"}
      >
        <div className="max-w-7xl mx-auto">

          {/* ① Project Meta */}
          <div className="flex flex-wrap gap-12 mb-16 border-b border-gray-300 pb-10">
            {[
              { label: "Role", value: "UX / Product Designer" },
              { label: "Timeline", value: "2026 · 12 Weeks" },
              { label: "Platform", value: "Mobile App & CarPlay" },
            ].map((m) => (
              <div key={m.label}>
                <p className="text-xs uppercase tracking-widest mb-1 font-sans text-gray-500">{m.label}</p>
                <p className="text-black font-medium font-sans">{m.value}</p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <SectionLabel title="Project Overview" />
              <blockquote className="border-l-4 border-[#8B96D4] pl-6 text-base sm:text-lg md:text-xl font-serif italic text-black">
                Does driving faster really give people more control — or does it reduce their ability to respond and make safe choices?
              </blockquote>
            </div>
            <div className="space-y-5 text-lg leading-relaxed text-gray-700 font-sans">
              <p>
                Slow is Fast is a driving behaviour companion tied to an insurance surcharge waiver program. Instead of warning or punishing drivers, it helps them recognize their own speeding patterns through personalized pre-drive briefs, minimal in-trip cues, post-trip reflection, and a 30-day final report.
              </p>
              <p>
                Rather than treating drivers as reckless, the product is designed to build awareness over time, through reflection, consistency, and practice.
              </p>
            </div>
          </div>

        </div>
      </motion.section>

      {/* ── The Challenge ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Context</p>
          <div className="mb-10 md:mb-14">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-serif font-bold text-black">
              Speeding isn't a rule problem.<br />It's a perception problem.
            </h2>
          </div>

          <img src="/sif-progress.png" alt="Research Analysis" className="w-full h-auto rounded-2xl mb-12" />

          <div className="space-y-6 text-lg leading-relaxed text-gray-700 font-sans mt-2">
            <p>
              Speeding is often framed as a rule-breaking problem, but our research told a different story. Most drivers who speed don't see themselves as reckless. As one participant put it: <span className="italic text-gray-500">"I was going with traffic. The road was empty. I just didn't notice how fast I was going."</span>
            </p>
            <p>
              <span className="font-bold text-black">56%</span> of speeding behaviours are neutral in sentiment: not deliberate, not emotional. Just situational. Speed cameras punish after the fact. Warning apps create noise. Neither changes how drivers actually think about speed.
            </p>
            <p>
              This created the central design challenge: how might a product help drivers rethink the relationship between speed and control, without feeling like surveillance or punishment?
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── What We Found ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Research</p>
          <SectionLabel title="What We Found" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20 items-start">
            <div className="space-y-6">
              <p className="text-[56px] sm:text-[80px] md:text-[100px] font-serif font-bold text-[#8B96D4] leading-none">56%</p>
              <p className="text-gray-600 font-sans text-lg leading-relaxed">
                of speeding behaviours are neutral in sentiment. Drivers don't see speeding as good or bad.<br />It depends on the situation.
              </p>
            </div>
            <div className="space-y-8">
              {[
                { n: "01", title: "Safety Perception", body: "Every driver defines 'safe' differently." },
                { n: "02", title: "Unconscious Behaviour", body: "Most speeding happens without intent." },
                { n: "03", title: "Social Norms", body: "Traffic flow overrides individual judgment." },
              ].map((item) => (
                <div key={item.n} className="flex gap-6 items-start">
                  <span className="text-[#8B96D4] font-serif font-bold text-4xl w-14 flex-shrink-0">{item.n}</span>
                  <div>
                    <p className="font-serif font-bold text-black text-2xl">{item.title}</p>
                    <p className="text-gray-500 text-base font-sans mt-1">{item.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* ── Design Response ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Design Response</p>
          <SectionLabel title="A Mirror, Not a Judge" />
          <div className="space-y-5 text-lg leading-relaxed text-gray-700 font-sans">
            <p>
              The design response was to show drivers their own patterns, not lecture them. The product acts as a mirror: neutral, personal, non-judgmental, reflecting behaviour back without attaching blame.
            </p>
            <p>
              Behaviour change rarely happens mid-action. The design targets the moments before and after a trip: the brief before departure and the reflection after arrival, when a driver is stationary and actually able to process what they're seeing.
            </p>
            <p>
              Research consistently showed that fines motivate more than safety appeals. Tying the product to a real financial outcome, the insurance surcharge waiver, gives users a concrete, personal reason to engage and stay consistent over 30 days.
            </p>
          </div>
        </div>
      </motion.section>

      {/* ── Ideation ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Ideation</p>
          <SectionLabel title="Early Exploration" />
          <img src="/sif-sketch.png" alt="Ideation Sketches" className="w-full h-auto rounded-2xl mt-8 mb-8" />
          <p className="text-lg leading-relaxed text-gray-700 font-sans">
            Before wireframes, the design process began with rough sketches: mapping the emotional arc of a drive, diagramming how data would flow through the system, and exploring what the key screens might feel like at a structural level. These early explorations shaped the core product decisions.
          </p>
        </div>
      </motion.section>

      {/* ── Meet Marcus ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">User Story</p>
          <SectionLabel title="Meet Marcus — Our Primary User" />

          {/* The Story */}
          <div className="mb-14 space-y-5 text-lg leading-relaxed text-gray-700 font-sans">
            <p className="text-xs uppercase tracking-widest text-gray-400 mb-6">The Story</p>
            <p>
              Marcus just moved to a new city. The roads are faster, the highway interchanges unfamiliar. Three weeks in, a $280 speeding ticket arrives, along with a warning that his insurance rate will rise at renewal. He doesn't think of himself as a reckless driver. He just didn't know the road.
            </p>
            <p>
              His insurer offers an alternative: complete a driving awareness program, maintain a qualifying safety score for 30 days, and the surcharge is waived. That program is Slow is Fast.
            </p>
            <p>
              Each commute begins with a 20-second brief, personalized to his route, the weather, and the habits the app has noticed. After every trip, a short summary shows him what happened and why. He sets a weekly focus goal, asks the AI follow-up questions, watches his score climb. At the end of 30 days, the surcharge is waived. The habits stay.
            </p>
          </div>
          <div className="flex flex-col md:flex-row gap-10 md:gap-24 items-stretch">

            {/* Left: Image + Name + bullets */}
            <div className="flex-shrink-0 w-52 flex flex-col gap-4">
              <img src="/marcus.png" alt="Marcus" className="h-32 object-contain self-center" />
              <div>
                <p className="font-serif text-2xl font-bold text-black text-center">Marcus</p>
                <p className="text-base text-[#8B96D4] font-sans mt-0.5 text-center">28 · New city resident</p>
              </div>
              <ul className="space-y-1.5 text-base text-gray-600 font-sans">
                {[
                  "First speeding ticket",
                  "Referred by insurer",
                  "Daily highway commuter",
                  "Uses GPS navigation",
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#8B96D4] flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            {/* Divider */}
            <div className="hidden md:block w-px border-l border-dashed border-gray-300 self-stretch" />

            {/* Right: Tags — stretch to match left column height */}
            <div className="flex-1 flex flex-col justify-between">
              {[
                { label: "Frustration", color: "bg-red-500", body: "Feels unfairly penalized. Wasn't being reckless, just unfamiliar with the roads." },
                { label: "Goal", color: "bg-blue-500", body: "Avoid the insurance surcharge and build confidence on new routes." },
                { label: "Behavior", color: "bg-green-600", body: "Checks phone before driving. Uses GPS. Mostly highway." },
                { label: "Openness", color: "bg-orange-400", body: "Open to change, as long as it feels like a tool, not surveillance." },
              ].map((row) => (
                <div key={row.label} className="flex gap-4 items-center py-3 border-b border-gray-200 last:border-0">
                  <span className={`${row.color} text-white text-sm font-sans font-semibold w-28 text-center py-2 rounded-md flex-shrink-0`}>
                    {row.label}
                  </span>
                  <p className="text-base text-gray-700 font-sans leading-relaxed">{row.body}</p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </motion.section>


      {/* ── User Flow ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">User Flow</p>
          <SectionLabel title="From Driving to Awareness" />
          <img src="/sif-userflow.png" alt="Slow is Fast User Flow" className="w-4/5 mx-auto h-auto rounded-2xl mt-8 mb-8" />
          <p className="text-lg leading-relaxed text-gray-700 font-sans">
            The user flow maps the full journey from opening the app to submitting a Final Report. The core loop runs across every trip: a pre-drive brief sets context, the drive itself is monitored with minimal interruption, and a post-trip score creates a moment for reflection. Over 30 days, this loop builds the habit that earns the insurance surcharge waiver. The AI Coach sits alongside the entire journey, learning patterns and surfacing insights on demand.
          </p>
        </div>
      </motion.section>

      {/* ── Wireframes ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Wireframes</p>
          <SectionLabel title="Wireframes" />
          <img src="/sif-wireframe.png" alt="Slow is Fast Wireframes" className="w-full h-auto rounded-2xl mt-8 mb-8" />
          <p className="text-lg leading-relaxed text-gray-700 font-sans mt-8">
            Wireframes were built screen-by-screen to map the full user journey, from onboarding and pre-drive briefing through in-trip monitoring, post-trip reflection, and the 30-day final report. The focus at this stage was structure and flow: establishing how each screen connected before any visual design was applied.
          </p>
        </div>
      </motion.section>

      {/* ── Onboarding + Core Features ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] pt-8 sm:pt-10 md:pt-12"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.02 }}
      >
        {/* Header */}
        <div className="px-4 sm:px-8 md:px-16 mb-12">
          <div className="max-w-7xl mx-auto">
            <Divider />
            <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Product Overview</p>
            <SectionLabel title="Product Design" />
          </div>
        </div>

        {/* Onboarding */}
        <div className="mb-20">
          <div className="px-8 sm:px-16 md:px-32"><img src="/sif-2.png" alt="Onboarding" className="w-full h-auto rounded-2xl" /></div>
          <div className="px-8 sm:px-16 md:px-32 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-8">
              <div className="md:border-r md:border-dashed md:border-[#8B96D4] md:pr-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans">Onboarding</p>
              </div>
              <p className="text-lg leading-relaxed text-gray-700 font-sans">
                The onboarding leads with the insurance surcharge waiver, not the features. Every screen is framed around the user's goal: Marcus isn't introduced to an app, he's shown a direct path to avoiding a financial penalty. The three steps (insurance context → Pre-Drive Brief preview → reflection intro) are designed to set expectations before the first trip, so the habit makes sense from day one.
              </p>
            </div>
          </div>
        </div>

        {/* Pre-Drive Brief */}
        <div className="mb-20">
          <div className="px-8 sm:px-16 md:px-32"><img src="/sif-3.png" alt="Pre-Drive Brief and In-Trip experience" className="w-full h-auto rounded-2xl" /></div>
          <div className="px-8 sm:px-16 md:px-32 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-8">
              <div className="md:border-r md:border-dashed md:border-[#8B96D4] md:pr-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans">Pre-Drive Brief · In-Trip</p>
              </div>
              <p className="text-lg leading-relaxed text-gray-700 font-sans">
                The brief appears as a map overlay before navigation starts, around 15 to 20 seconds, personalized to route and past behaviour. It's the only moment when a driver is stationary and open to reflection. Once the drive begins, the app goes quiet. No stream of alerts. Only two triggers surface mid-trip: entering a known risk zone, or a physical vehicle event. Fewer cues mean each one actually registers.
              </p>
            </div>
          </div>
        </div>

        {/* Post-Trip Reflection */}
        <div className="mb-20">
          <div className="px-8 sm:px-16 md:px-32"><img src="/sif-4.png" alt="Post-Trip Reflection — Trip Score breakdown" className="w-full h-auto rounded-2xl" /></div>
          <div className="px-8 sm:px-16 md:px-32 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-8">
              <div className="md:border-r md:border-dashed md:border-[#8B96D4] md:pr-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans">Post-Trip Reflection</p>
              </div>
              <p className="text-lg leading-relaxed text-gray-700 font-sans">
                After every trip, a score is broken down across three dimensions: Speed Consistency, Behaviour in High-Risk Zones, and Contextual Adaptation. The breakdown is designed to make invisible patterns concrete, not to judge, but to show. An AI Coach prompt tied to that specific trip's data offers one actionable thought, grounded in what actually happened, not a generic tip.
              </p>
            </div>
          </div>
        </div>

        {/* Final Report */}
        <div className="pb-8 sm:pb-10 md:pb-12">
          <div className="px-8 sm:px-16 md:px-32"><img src="/sif-5.png" alt="Final Report — 30-day summary" className="w-full h-auto rounded-2xl" /></div>
          <div className="px-8 sm:px-16 md:px-32 mt-6">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr] gap-4 md:gap-8">
              <div className="md:border-r md:border-dashed md:border-[#8B96D4] md:pr-8">
                <p className="text-xs uppercase tracking-widest text-gray-400 font-sans">30-Day Final Report</p>
              </div>
              <p className="text-lg leading-relaxed text-gray-700 font-sans">
                At the end of the programme, the app generates a Final Report: an overall score out of 100, a trend line across the 30 days, and a set of personalised focus goals. The report is exportable as PDF, serving as the tangible proof a user needs to submit for their insurance surcharge waiver. The report is the destination the entire product builds toward; it gives the 30-day structure its purpose.
              </p>
            </div>
          </div>
        </div>

      </motion.section>

      {/* ── CarPlay ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] pt-8 sm:pt-10 md:pt-12"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.02 }}
      >
        {/* Header */}
        <div className="px-4 sm:px-8 md:px-16 mb-12">
          <div className="max-w-7xl mx-auto">
            <Divider />
            <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-4">Extended Platform</p>
            <SectionLabel title="CarPlay" />
          </div>
        </div>

        {/* Auto-sliding CarPlay images */}
        <CarPlaySlider />

        {/* Caption */}
        <div className="px-4 sm:px-8 md:px-16 pb-8 sm:pb-10 md:pb-12">
          <div className="max-w-7xl mx-auto">
            <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-2">CarPlay Integration</p>
            <p className="text-lg leading-relaxed text-gray-700 font-sans">
              The CarPlay extension brings Slow is Fast into the dashboard without adding distraction. The pre-drive brief is read aloud before the route begins. During the trip, a single ambient indicator replaces the phone screen entirely, with no tapping or glancing. The design principle is the same as the mobile app: less interaction, not more. CarPlay is where the product disappears into the drive.
            </p>
          </div>
        </div>

      </motion.section>


      {/* ── 9. Testing and Iteration ── */}
      <motion.section
        className="w-full bg-[#F5F5F0] py-8 sm:py-10 md:py-12 pb-20 md:pb-28 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider />
          <SectionLabel title="Testing and Iteration" />
          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-8">5 participants · Moderated usability testing · Prototype stage</p>

          <img src="/sif-usertesting.png" alt="User Testing Results" className="w-full h-auto rounded-2xl mb-12" />

          <div className="space-y-5 text-lg leading-relaxed text-gray-700 font-sans mb-16">
            <p>
              The pre-drive brief landed clearly: participants understood its purpose and found it meaningful. In-trip alerts were understandable, and the overall value proposition made sense once onboarding was complete. The brief stood out as the feature most likely to actually change real driving behaviour.
            </p>
            <p>
              The harder findings were around score comprehension: users didn't understand how the score was calculated, and the labels felt abstract. Onboarding wasn't doing enough to explain the insurance context. And without a stronger daily hook, motivation faded after the first few trips.
            </p>
            <p>
              These findings drove the most significant design changes: adding the breakdown panel after every trip, rewriting onboarding to lead with the surcharge waiver, grounding the AI Coach in trip-specific data, and simplifying the report structure to Trip Summary + Final Report.
            </p>
          </div>

          <p className="text-xs uppercase tracking-widest text-gray-400 font-sans mb-8">What we'd explore next</p>
          <div className="space-y-10">
            {[
              {
                n: "01",
                title: "Is scoring the right language?",
                body: "Does a number create pressure rather than awareness? Alternatives worth exploring: checkboxes, streaks, pattern summaries.",
              },
              {
                n: "02",
                title: "What does the AI actually owe users?",
                body: "Users expected real-time guidance, but we built a post-trip coach. Both are valid, but they're different products.",
              },
              {
                n: "03",
                title: "Where does trust begin?",
                body: "Users shared data when the benefit was concrete and control was theirs. This applies to insurers, to the AI, and to the product itself.",
              },
            ].map((item) => (
              <div key={item.n} className="flex gap-8 items-start">
                <span className="text-[#8B96D4] font-serif font-bold text-4xl flex-shrink-0 w-14">{item.n}</span>
                <div>
                  <p className="font-serif font-bold text-black text-2xl mb-2">{item.title}</p>
                  <p className="text-gray-500 text-lg font-sans leading-relaxed">{item.body}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </motion.section>

      {/* ── 10. Reflection ── */}
      <motion.section
        className="w-full bg-black py-8 sm:py-10 md:py-12 px-4 sm:px-8 md:px-16"
        variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }}
      >
        <div className="max-w-7xl mx-auto">
          <Divider dark />
          <SectionLabel title="Reflection" dark />
          <div className="relative mb-14">
            <span className="absolute -top-6 -left-2 sm:-left-4 text-[72px] sm:text-[120px] leading-none font-serif text-[#8B96D4] select-none">"</span>
            <div className="space-y-5 text-lg leading-relaxed text-gray-300 font-sans pl-8">
              <p>
                Slow is Fast responds to the challenge not by confronting drivers, but by helping them notice their own patterns over time. The pre-drive brief interrupts unconscious habit before it starts. The trip summary makes invisible behaviour visible. The insurance-linked 30-day structure gives users a concrete reason to stay engaged.
              </p>
              <p>
                Instead of telling drivers to "just slow down," Slow is Fast offers a more reflective path: real control comes not from speed, but from awareness, choice, and consistency.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 border-t border-white/10 pt-12">
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-sans">What I'd do differently</p>
              <p className="text-lg leading-relaxed text-gray-300 font-sans">
                One clear tension surfaced in our first round of testing: users wanted the AI Coach to guide them in real time, mid-drive, in the moment. But the product is deliberately built around post-trip reflection, not live intervention. These are two valid but fundamentally different products. Resolving that gap is the central question for the next iteration.
              </p>
            </div>
            <div className="space-y-4">
              <p className="text-xs uppercase tracking-widest text-gray-500 font-sans">What this project taught me</p>
              <p className="text-lg leading-relaxed text-gray-300 font-sans">
                Designing for behaviour change is fundamentally different from designing for task completion. Users aren't trying to use the app. They're trying to become a different kind of driver. That shift in framing changed how I thought about every interaction: less about efficiency, more about whether the moment actually creates space for reflection.
              </p>
            </div>
          </div>
        </div>

        {/* ④ Full-width video demo */}
        <VideoPlayer videoId="evKN7jQKaaY" title="Slow is Fast — Prototype Demo" />
      </motion.section>

      {/* ── VIEW ALL PROJECTS ── */}
      <div className="flex justify-center py-16 md:py-24 bg-[#F5F5F0]">
        <Link
          to="/all-projects"
          onClick={() => { window.scrollTo(0, 0); }}
          className="bg-[#E2E5F8] text-black border border-black/90 px-8 sm:px-12 md:px-20 py-3 sm:py-4 font-serif text-sm sm:text-base font-medium transition-all duration-300 ease-in-out hover:-translate-y-1 hover:shadow-[0_8px_0_0_rgba(0,0,0,0.8)] hover:bg-[#C8CDEF] active:translate-y-0 active:shadow-[0_1px_0_0_rgba(0,0,0,0.8)] rounded-[20px]"
        >
          VIEW ALL PROJECTS
        </Link>
      </div>

      <Footer />
    </div>
  );
};

export default SlowisFast;
